import React, { useEffect, useMemo, useState } from "react";
import StyledModal from "../../components/CustomModal";
import { ColorPicker, Form, FormInstance, Input, Switch, Collapse, message, Tooltip } from "antd";
import { useAppSelector } from "../../utils/hooks/redux";
import { sanitizeHtml } from "../../utils/helpers/sanitizeHtml";
import { FooterButtons, FormWrapper, StyledButton } from "../../components/styles";
import { EmailPreviewContainer, InfoText, SwitchContainer, SwitchLabel, VariableTag } from "./styles";
import type { ColorPickerProps, GetProp } from "antd";
import { selectEmailTemplate } from "../../redux/features/emailTemplate/emailTemplateSlice";
import { CheckOutlined, CloseOutlined, CopyOutlined } from "@ant-design/icons";
import { variableCategories } from "./EmailTemplateConst";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import { ResizableContainer } from "../ReportDetails/ReportDetailsFooter/styles";

type EmailTemplateModalProps = {
  isModalOpen: boolean;
  handleSave: (values: any) => void;
  onCancel: () => void;
  form: FormInstance<any>;
  hasEditPermission?: boolean;
  hasViewPermission?: boolean;
  hasAddPermission?: boolean;
};

type Color = Extract<GetProp<ColorPickerProps, "value">, string | { cleared: any }>;

const EmailTemplateModal: React.FC<EmailTemplateModalProps> = ({ isModalOpen, handleSave, onCancel, form, hasEditPermission, hasViewPermission, hasAddPermission }) => {
  const { loading } = useAppSelector(selectEmailTemplate);
  const [previewBody, setPreviewBody] = useState<string>("");

  useEffect(() => {
    if (isModalOpen) {
      setPreviewBody(form.getFieldValue("body") || "");
    }
  }, [isModalOpen, form]);

  const handleColorChange = (colorHex: Color) => {
    const color = typeof colorHex === "string" ? colorHex : colorHex?.toHexString();
    form.setFieldsValue({ color: color });
  };

  const handleSwitchChange = (checked: boolean) => {
    form.setFieldsValue({
      status: checked,
    });
  };

  const copyVariable = (variable: string) => {
    navigator.clipboard.writeText(`{{${variable}}}`);
    message.success({ content: `Copied {{${variable}}}`, duration: 1.5 });
  };

  const CopyableTag = ({ name }: { name: string }) => (
    <Tooltip title="Click to copy">
      <VariableTag
        color="processing"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => copyVariable(name)}
      >
        <CopyOutlined style={{ fontSize: 10, marginRight: 3 }} />
        {`{{${name}}}`}
      </VariableTag>
    </Tooltip>
  );

  const canSaveChanges = useMemo(() => hasAddPermission || hasEditPermission, [hasAddPermission, hasEditPermission]);

  return (
    <StyledModal
      draggable={true}
      width={768}
      title={form.getFieldValue("id") ? `${canSaveChanges ? "Edit" : "View"} Email Template` : `${canSaveChanges ? "Add" : "View"} New Email Template`}
      open={isModalOpen}
      closeIcon={null}
      footer={
        <FooterButtons>
          <StyledButton onClick={onCancel} disabled={loading}>
            Cancel
          </StyledButton>
          {canSaveChanges && <StyledButton
            type="primary"
            onClick={async () => {
              try {
                const values = await form.validateFields(["longTitle", "shortTitle", "color", "subject", "body", "signature", "isNotToGuest", "status"]);
                handleSave(values);
              } catch (error) {
                console.error("Validation failed:", error);
              }
            }}
            loading={loading}
          >
            Save Template
          </StyledButton>}
        </FooterButtons>
      }
      centered
      onCancel={() => !loading && onCancel()}
    >
      <FormWrapper form={form} layout="vertical" disabled={!canSaveChanges}>
        <Form.Item label="Template Name" name="longTitle" rules={[{ required: true, message: "Please enter email template name." }]}>
          <Input size="large" disabled={loading || !canSaveChanges} />
        </Form.Item>

        <Form.Item label="Short Name" name="shortTitle" rules={[{ required: true, message: "Please enter email short name." }]}>
          <Input size="large" disabled={loading || !canSaveChanges} />
        </Form.Item>

        <Form.Item label="Template Accent Color" name="color" initialValue="#1677ff" rules={[{ required: true, message: "Please select email template accent color." }]}>
          <ColorPicker disabled={loading || !canSaveChanges} defaultValue="#1677ff" size="large" onChange={handleColorChange} />
        </Form.Item>

        <Form.Item label="Subject" name="subject" rules={[{ required: true, message: "Please enter email subject." }]}>
          <Input size="large" disabled={loading || !canSaveChanges} />
        </Form.Item>

        <Form.Item
          label="Body"
          name="body"
          rules={[
            { required: true, message: "" },
            {
              validator: (_, value) => {
                // Remove HTML tags and whitespace to check if there's actual content
                const textContent = value?.replace(/<[^>]*>/g, '').trim();
                if (!textContent) {
                  return Promise.reject(new Error('Please enter email body.'));
                }
                return Promise.resolve();
              }
            }
          ]}
          validateTrigger={['onChange', 'onBlur']}
        >
          <ResizableContainer>
            {React.createElement(ReactQuill as any, {
              theme: "snow",
              style: { height: "100%" },
              readOnly: loading || !canSaveChanges,
              onChange: (content: string) => {
                form.setFieldsValue({ body: content });
                setPreviewBody(content);
                // Trigger validation when content changes
                form.validateFields(['body']);
              },
              onBlur: () => {
                // Trigger validation when editor loses focus
                form.validateFields(['body']);
              },
              value: previewBody
            })}
          </ResizableContainer>
        </Form.Item>
        <div style={{ marginTop: 8, marginBottom: 12 }}>
          <Collapse size="small" items={[
            ...variableCategories.map((cat, i) => ({
              key: String(i),
              label: <span style={{ fontSize: 12, fontWeight: 600 }}>{cat.label}</span>,
              children: (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {cat.vars.map((v) => <CopyableTag key={v} name={v} />)}
                </div>
              ),
            })),
            {
              key: "qa",
              label: <span style={{ fontSize: 12, fontWeight: 600 }}>Question Answers <span style={{ color: "#6b7280", fontWeight: 400 }}>(use exact question field name)</span></span>,
              children: (
                <div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                    Use <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>{`{{Q:Question Name}}`}</code> where "Question Name" is the exact field name from your Establishment Type Questions setup.
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    <CopyableTag name="Q:Flight Number" />
                    <CopyableTag name="Q:Airline" />
                    <CopyableTag name="Q:Terminal" />
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                    These are examples. Replace with your actual question field names.
                  </div>
                </div>
              ),
            },
          ]} />
        </div>
        <div style={{ marginTop: "1.5rem" }}>
          <h4 style={{ marginBottom: "0.5rem" }}>Body Preview</h4>
          <EmailPreviewContainer
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewBody) }}
          />
        </div>
        <Form.Item label="Signature" name="signature" rules={[{ required: true, message: "Please enter signature." }]}>
          <Input.TextArea rows={6} size="large" disabled={loading || !canSaveChanges} />
        </Form.Item>
        <InfoText>All html tags are allowed.</InfoText>

        <Form.Item name="isNotToGuest" valuePropName="checked" initialValue={false}>
          <SwitchContainer>
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
            <SwitchLabel>Not to Guest <span style={{ color: "#9ca3af", fontWeight: 400 }}>(internal only, won't be sent to guest email)</span></SwitchLabel>
          </SwitchContainer>
        </Form.Item>

        <Form.Item name="status" valuePropName="checked" initialValue={true}>
          <SwitchContainer>
            <Switch defaultChecked checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} onChange={handleSwitchChange} />
            <SwitchLabel>Show in individual claim</SwitchLabel>
          </SwitchContainer>
        </Form.Item>

      </FormWrapper>
    </StyledModal>
  );
};

export default EmailTemplateModal;
